import Vue from "vue";
import { shrinkImage } from "@/utils/imageShrink";

//set Headers payload
const authHeader = process.env.VUE_APP_REQUEST_HEADER;
const headers = {};
headers[authHeader] = process.env.VUE_APP_HUB_API_KEY;

const base = () => process.env.VUE_APP_HUB_API_HOST + "api/manufacturing/works";

function parseJson(value) {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function parseWork(work) {
  work.contract_detalii = parseJson(work.contract_detalii);
  if (work.gard_obj !== undefined) work.gard_obj = parseJson(work.gard_obj) || [];
  if (work.alte_obj !== undefined) work.alte_obj = parseJson(work.alte_obj) || [];
  return work;
}

// An observation call answers with { observations, work }: the list, and the work's row.
function commitObservations(commit, workId, body) {
  commit("setWorkObservations", { workId, items: body.observations });
  if (body.work) commit("mergeWork", body.work);
}

// PUT a file to its signed storage link. Straight to storage, so none of the API's headers;
// onProgress gets 0-100.
function putToStorage(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(xhr));
    xhr.onerror = () => reject(xhr);
    xhr.send(file);
  });
}

// The API answers errors with { error }; surface it and rethrow so callers can stay open.
function fail(error) {
  const message = (error.body && error.body.error) || "Eroare la comunicarea cu serverul";
  M.toast({ html: message, classes: "red darken-1" });
  throw error;
}

export default {
  namespaced: true,
  state: () => ({
    loadingWorks: false,
    works: [],
    loadingWork: false,
    work: {},
    loadingEvents: false,
    events: [],
    agents: [],
    // OameniProductie by tip: production and montage are done by different people
    people: { productie: [], montaj: [] },
    loadingPeople: false,
    settings: null,
    // the Lucrari list's filters, kept while the app is open so they survive switching screens
    listFilters: null,
  }),
  mutations: {
    setLoadingWorks(state, value) {
      state.loadingWorks = value;
    },
    setWorks(state, items) {
      state.works = items.map(parseWork);
    },
    setLoadingWork(state, value) {
      state.loadingWork = value;
    },
    setWork(state, item) {
      state.work = parseWork(item);
    },
    // A stage change answers with the bare Works row; merge it into what is on screen.
    mergeWork(state, row) {
      if (state.work.id_work == row.id_work) {
        state.work = { ...state.work, ...row };
      }
      const idx = state.works.findIndex((w) => w.id_work == row.id_work);
      if (idx > -1) {
        state.works.splice(idx, 1, { ...state.works[idx], ...row });
      }
    },
    // an observation call answers with the work's whole list (and its row, see commitObservations)
    setWorkObservations(state, { workId, items }) {
      if (state.work.id_work == workId) {
        state.work = { ...state.work, observations: items };
      }
    },
    setWorkFiles(state, { workId, items }) {
      if (state.work.id_work == workId) {
        state.work = { ...state.work, files: items };
      }
    },
    setLoadingEvents(state, value) {
      state.loadingEvents = value;
    },
    setEvents(state, items) {
      state.events = items;
    },
    setPeople(state, { tip, items }) {
      state.people = { ...state.people, [tip]: items };
    },
    setLoadingPeople(state, value) {
      state.loadingPeople = value;
    },
    setAgents(state, items) {
      state.agents = items;
    },
    setListFilters(state, filters) {
      state.listFilters = filters;
    },
    setSettings(state, settings) {
      state.settings = settings;
    },
  },
  actions: {
    // filters: { stage, userId, country, includeClosed }
    fetchWorks({ commit }, filters = {}) {
      commit("setLoadingWorks", true);
      return Vue.http
        .get(base(), { headers, params: filters })
        .then((response) => {
          commit("setWorks", response.body);
        })
        .catch(fail)
        .finally(() => commit("setLoadingWorks", false));
    },
    fetchWork({ commit }, id) {
      commit("setLoadingWork", true);
      return Vue.http
        .get(base() + "/" + id, { headers })
        .then((response) => {
          commit("setWork", response.body);
        })
        .catch(fail)
        .finally(() => commit("setLoadingWork", false));
    },
    // The work of one offer or contract, or null. Not stored: the caller shows it.
    findWork(context, params) {
      return Vue.http
        .get(base(), { headers, params })
        .then((response) => (response.body.length ? parseWork(response.body[0]) : null))
        .catch(() => null);
    },
    // details: what the action records, e.g. montaj_probleme / montaj_observatii for montaj_finalizat
    changeStage({ commit, rootGetters }, { id, action, stage, ...details }) {
      // user_id: who did it - montage problems become an intervention in their name
      const payload = { action, stage, ...details, user_id: rootGetters.loggedUser.id_user };
      return Vue.http
        .put(base() + "/" + id + "/stage", payload, { headers })
        .then((response) => {
          commit("mergeWork", response.body);
          M.toast({ html: "Stadiu actualizat", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    // fields: vopsire_externa (the whole work goes out to be painted), vopsire_elemente (text,
    // single elements to paint) and each part's switches - vopsire_trimisa / vopsire_returnata,
    // elemente_trimise / elemente_returnate (the API records the day). Only what is passed is changed.
    updateVopsire({ commit }, { id, ...fields }) {
      return Vue.http
        .put(base() + "/" + id + "/vopsire", fields, { headers })
        .then((response) => {
          commit("mergeWork", response.body);
          M.toast({ html: "Vopsire externa actualizata", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    // Postpone (with a reason) or resume the work; it keeps its stage.
    updateAmanare({ commit, rootGetters }, { id, amanata, motiv }) {
      return Vue.http
        .put(base() + "/" + id + "/amanare", { amanata, motiv, user_id: rootGetters.loggedUser.id_user }, { headers })
        .then((response) => {
          commit("mergeWork", response.body);
          M.toast({ html: amanata ? "Lucrare amanata" : "Lucrare reluata", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    // Observations: add, resolve / reopen, delete. Each answers with the work's list.
    // kind: observatie | interventie (a problem the client reports, mostly after the work is finished)
    addObservation({ commit, rootGetters }, { workId, kind = "observatie", text, message }) {
      return Vue.http
        .post(base() + "/" + workId + "/observations", { kind, text, user_id: rootGetters.loggedUser.id_user }, { headers })
        .then((response) => {
          commitObservations(commit, workId, response.body);
          M.toast({ html: message || "Adaugat", classes: "bg-green-500" });
        })
        .catch(fail);
    },
    resolveObservation({ commit, rootGetters }, { workId, id, resolved }) {
      return Vue.http
        .put(base() + "/observations/" + id, { resolved, user_id: rootGetters.loggedUser.id_user }, { headers })
        .then((response) => {
          commitObservations(commit, workId, response.body);
        })
        .catch(fail);
    },
    deleteObservation({ commit, rootGetters }, { workId, id }) {
      return Vue.http
        .delete(base() + "/observations/" + id, { headers, params: { user_id: rootGetters.loggedUser.id_user } })
        .then((response) => {
          commitObservations(commit, workId, response.body);
          M.toast({ html: "Observatie stearsa", classes: "bg-green-500" });
        })
        .catch(fail);
    },
    // Files: kind masuratori (the measurement sheet) | imagine (photos of the site). Photos are
    // shrunk first; the API hands a signed link, the file goes straight to storage, then the API
    // records it. Each step's error is shown and rethrown.
    // baseName: the name to save it under, without extension (the shrunk photo's .jpg is added).
    async uploadWorkFile({ commit, rootGetters }, { workId, kind, file, baseName, onProgress }) {
      let upload = kind === "masuratori"
        ? await shrinkImage(file, { maxSide: 2600, quality: 0.85 }) // the handwriting stays legible
        : await shrinkImage(file);
      if (baseName) {
        const ext = (upload.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
        upload = new File([upload], baseName + ext, { type: upload.type });
      }
      const info = { kind, name: upload.name, type: upload.type, size: upload.size };
      const { body } = await Vue.http.post(base() + "/" + workId + "/files/upload-url", info, { headers }).catch(fail);
      try {
        await putToStorage(body.url, upload, onProgress);
      } catch (e) {
        M.toast({ html: "Fisierul nu a putut fi incarcat: " + upload.name, classes: "red darken-1" });
        throw e;
      }
      const response = await Vue.http
        .post(base() + "/" + workId + "/files", { ...info, key: body.key, user_id: rootGetters.loggedUser.id_user }, { headers })
        .catch(fail);
      commit("setWorkFiles", { workId, items: response.body.files });
    },
    deleteWorkFile({ commit }, { workId, id }) {
      return Vue.http
        .delete(base() + "/files/" + id, { headers })
        .then((response) => {
          commit("setWorkFiles", { workId, items: response.body.files });
          M.toast({ html: "Fisier sters", classes: "bg-green-500" });
        })
        .catch(fail);
    },
    updateDetalii({ commit }, { id, done }) {
      return Vue.http
        .put(base() + "/" + id + "/detalii", { detalii_stabilite: done }, { headers })
        .then((response) => {
          commit("mergeWork", response.body);
          M.toast({ html: done ? "Detalii marcate ca stabilite" : "Detalii marcate ca nestabilite", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    updateDeadline({ commit }, { id, date }) {
      return Vue.http
        .put(base() + "/" + id + "/deadline", { data_montaj_maxima: date || null }, { headers })
        .then((response) => {
          commit("mergeWork", response.body);
          M.toast({ html: "Data montaj maxima actualizata", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    createFromContract(context, contractId) {
      return Vue.http
        .post(base() + "/from-contract/" + contractId, {}, { headers })
        .then((response) => response.body.id_work)
        .catch(fail);
    },
    // { type, from, to } or { workId }
    fetchEvents({ commit }, params) {
      commit("setLoadingEvents", true);
      return Vue.http
        .get(base() + "/events", { headers, params })
        .then((response) => {
          commit("setEvents", response.body);
          return response.body;
        })
        .catch(fail)
        .finally(() => commit("setLoadingEvents", false));
    },
    createEvent(context, payload) {
      return Vue.http
        .post(base() + "/events", payload, { headers })
        .then((response) => {
          M.toast({ html: "Programare salvata", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    updateEvent(context, { id, ...payload }) {
      return Vue.http
        .put(base() + "/events/" + id, payload, { headers })
        .then((response) => {
          M.toast({ html: "Programare actualizata", classes: "bg-green-500" });
          return response.body;
        })
        .catch(fail);
    },
    deleteEvent(context, id) {
      return Vue.http
        .delete(base() + "/events/" + id, { headers })
        .then(() => {
          M.toast({ html: "Programare stearsa", classes: "bg-green-500" });
        })
        .catch(fail);
    },
    // Who can be sent to measure: the fence agents, whatever their role (one is a superuser).
    // Users.agent_market is set ('ro' / 'export') exactly for them and null for everyone else.
    fetchAgents({ commit, state }) {
      if (state.agents.length) return Promise.resolve();
      return Vue.http
        .get(process.env.VUE_APP_HUB_API_HOST + "api/users", { headers })
        .then((response) => {
          commit("setAgents", response.body.filter((u) => !!u.agent_market));
        })
        .catch(fail);
    },
    // { force } reloads; otherwise a list already in the store is reused.
    fetchPeople({ commit, state }, { tip, force = false }) {
      if (state.people[tip].length && !force) return Promise.resolve();
      commit("setLoadingPeople", true);
      return Vue.http
        .get(process.env.VUE_APP_HUB_API_HOST + "api/productie/oameni", { headers, params: { tip } })
        .then((response) => {
          commit("setPeople", { tip, items: response.body });
        })
        .catch(fail)
        .finally(() => commit("setLoadingPeople", false));
    },
    addPerson({ dispatch }, { tip, name, color }) {
      return Vue.http
        .post(process.env.VUE_APP_HUB_API_HOST + "api/productie/oameni/add", { tip, name, color }, { headers })
        .then((response) => {
          if (response.body.error) throw { body: response.body };
          M.toast({ html: "Persoana adaugata", classes: "bg-green-500" });
          return dispatch("fetchPeople", { tip, force: true });
        })
        .catch(fail);
    },
    deletePerson({ dispatch }, { tip, id }) {
      return Vue.http
        .delete(process.env.VUE_APP_HUB_API_HOST + "api/productie/oameni/delete/" + id, { headers })
        .then((response) => {
          if (response.body.error) throw { body: response.body };
          M.toast({ html: "Persoana stearsa", classes: "bg-green-500" });
          return dispatch("fetchPeople", { tip, force: true });
        })
        .catch(fail);
    },
    // People already booked in a timeframe (production or montage), to grey them out in the
    // dialog. params: { type, start, hours } or { type, start, end }, plus exclude = event id.
    fetchBusyPeople(context, params) {
      return Vue.http
        .get(base() + "/events/busy-people", { headers, params })
        .then((response) => response.body)
        .catch(() => []);
    },
    // Cached: the calendars and the dialog need the working day; { force } reloads.
    fetchSettings({ commit, state }, { force = false } = {}) {
      if (state.settings && !force) return Promise.resolve();
      return Vue.http
        .get(base() + "/settings", { headers })
        .then((response) => {
          commit("setSettings", response.body);
        })
        .catch(fail);
    },
    updateSettings({ commit }, settings) {
      return Vue.http
        .put(base() + "/settings", settings, { headers })
        .then((response) => {
          commit("setSettings", response.body);
          M.toast({ html: "Setari salvate", classes: "bg-green-500" });
        })
        .catch(fail);
    },
  },
};
